import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FilterOpenedEvent } from 'ag-grid-community';

import {
  formatClasses,
  formatClassesFilterValue,
  regionKeyCreator,
  regionSetFilterValueFormatter,
  regionsFilterValueGetter,
} from '../../columns/column-definition/set-filter-helpers';
import { getClassesValues } from '../../columns/column-definition/grid-options.service';
import { ClientReferenceMultiFilterComponent } from '../../columns/filtering/custom-filter-components/client-reference-multi-filter/client-reference-multi-filter.component';
import { SubClientReferenceMultiFilterComponent } from '../../columns/filtering/custom-filter-components/sub-client-reference-multi-filter/sub-client-reference-multi-filter.component';
import { enableFilterOptionTabFocus } from '../../columns/filtering/filter-option-tab-focus';
import {
  classComparator,
  FilterOptions,
  genericComparator,
  regionSetFilterComparator,
} from '../../sorting/sort-utils';
import { WatchOrderActionRendererComponent } from '../alert-columns/watch-order-action-renderer/watch-order-action-renderer.component';
import {
  emptyValueComparator,
  labelComparator,
} from '../utils/watch-alert-comparator.util';

@Component({
  selector: 'app-watch-alerts-orders-grid',
  templateUrl: './watch-alerts-orders-grid.component.html',
  styleUrls: ['./watch-alerts-orders-grid.component.scss'],
  standalone: false,
})
export class WatchAlertsOrdersGridComponent implements AfterViewInit {
  @Input() orders: any[] | null = null;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() parentGridApi: any = null;
  @Input() currentAlertId: string | null = null;
  @Output() gridHeightChange = new EventEmitter<number>();
  @Output() selectionChanged = new EventEmitter<any[]>();

  @ViewChild('watchOrdersGrid') private watchOrdersGrid?: ElementRef;

  public gridApi: any;

  public gridOptions: any = {
    theme: 'legacy',
    columnMenu: 'legacy',
    domLayout: 'autoHeight',
    suppressCellFocus: true,
    suppressContextMenu: true,
    components: {
      watchOrderActionRenderer: WatchOrderActionRendererComponent,
    },
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      sortable: true,
      filter: true,
      resizable: true,
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
    },
    columnDefs: [
      {
        headerName: '',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        resizable: false,
        maxWidth: 50,
        suppressHeaderMenuButton: true,
      },
      {
        field: 'trademarkName',
        headerName: 'Trademark Watched',
        comparator: labelComparator,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          cellHeight: 40,
        },
      },
      {
        headerName: 'Actions',
        cellRenderer: 'watchOrderActionRenderer',
        cellRendererParams: () => ({
          parentGridApi: this.parentGridApi,
          currentAlertId: this.currentAlertId,
        }),
        minWidth: 100,
        menuTabs: ['generalMenuTab'],
        sortable: false,
      },
      {
        field: 'productName',
        headerName: 'Product',
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          closeOnApply: true,
          buttons: ['apply', 'clear'],
          comparator: genericComparator,
        },
      },
      {
        field: 'clientReference',
        headerName: 'Your Reference',
        comparator: emptyValueComparator,
        filter: ClientReferenceMultiFilterComponent,
      },
      {
        field: 'subClientReference',
        headerName: 'Sub Client Reference',
        comparator: emptyValueComparator,
        filter: SubClientReferenceMultiFilterComponent,
      },
      {
        field: 'regions',
        headerName: 'Region',
        comparator: labelComparator,
        keyCreator: regionKeyCreator,
        filterValueGetter: regionsFilterValueGetter,
        valueGetter: (params: any) => {
          const regions = params.data?.regions;
          return Array.isArray(regions) ? this.formatRegions(regions) : '';
        },
        valueFormatter: (params: any) => {
          const val = params.value;
          if (Array.isArray(val)) {
            return this.formatRegions(val);
          }
          if (typeof val === 'string') {
            return val;
          }
          return '';
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          cellHeight: 40,
          comparator: regionSetFilterComparator,
          valueFormatter: regionSetFilterValueFormatter,
        },
      },
      {
        field: 'classes',
        headerName: 'Classes',
        comparator: classComparator,
        filter: 'agSetColumnFilter',
        valueFormatter: formatClasses,
        filterParams: {
          defaultToNothingSelected: true,
          closeOnApply: true,
          refreshValuesOnOpen: true,
          buttons: ['apply', 'clear'],
          filterOptions: FilterOptions.OPTIONS,
          values: getClassesValues,
          keyCreator: formatClassesFilterValue,
          valueFormatter: formatClassesFilterValue,
        },
      },
    ],
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    animateRows: true,
    onSelectionChanged: () => this.onSelectionChanged(),
    onFilterChanged: () => setTimeout(() => this.emitGridHeight(), 0),
    onFilterOpened: (event: FilterOpenedEvent) =>
      enableFilterOptionTabFocus(event, [
        'clientReference',
        'subClientReference',
      ]),
  };

  private formatRegions = (regions: any[]): string => {
    return regions
      .filter((region: any) => region !== null && region !== undefined)
      .map((r: any) => ({
        label: String(r.label || '').trim(),
        limited: !!r.limited,
      }))
      .filter((r: any) => r.label !== '')
      .sort((a: any, b: any) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
      )
      .map((r: any) => r.label + (r.limited ? ' (Limited)' : ''))
      .join(', ');
  };

  onGridReady(params: any): void {
    this.gridApi = params.api;
    params.api.sizeColumnsToFit();
    setTimeout(() => this.emitGridHeight(), 0);
  }

  onSelectionChanged(): void {
    if (this.gridApi) {
      const selectedRows = this.gridApi.getSelectedRows();
      this.selectionChanged.emit(selectedRows);
    }
  }

  emitGridHeight(): void {
    const height = this.watchOrdersGrid?.nativeElement?.offsetHeight;
    this.gridHeightChange.emit(height > 0 ? height : 400);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.gridApi) {
        this.emitGridHeight();
      }
    });
  }
}
